import { useState, useEffect, useRef } from 'react';
import { FaTrophy } from 'react-icons/fa';
import quranApi from '../../api/quranApi';
import AyahDisplay from './AyahDisplay';
import apiClient from '../../api/apiClient';

const QuranReader = ({
    surahNumber,
    initialAyah = 1,
    reciterId,
    onProgressUpdate,
    bookmarks = [],
    setBookmarks,
    showCompletionModal,
    setShowCompletionModal,
}) => {
    const [surahData, setSurahData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [playingAyahIndex, setPlayingAyahIndex] = useState(null);
    const ayahRefs = useRef({});

    useEffect(() => {
        const fetchSurah = async () => {
            setLoading(true);
            try {
                const data = await quranApi.getSurahDetails(surahNumber);
                // Merge Arabic and Translation
                // data[0] is Arabic, data[1] is Translation
                const arabicText = data[0].ayahs;
                const translationText = data[1].ayahs;

                const mergedAyahs = arabicText.map((ayah, index) => {
                    let text = ayah.text;
                    // Remove Bismillah from the first ayah of all surahs except Al-Faatiha (1) and At-Tawbah (9)
                    if (surahNumber !== 1 && surahNumber !== 9 && index === 0) {
                        const bismillah = "بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ";
                        if (text.startsWith(bismillah)) {
                            text = text.replace(bismillah, '').trim();
                        }
                    }
                    return {
                        ...ayah,
                        text, // Use cleaned text
                        translation: translationText[index].text,
                    };
                });

                setSurahData({ ...data[0], ayahs: mergedAyahs });
                setLoading(false);
            } catch (error) {
                console.error('Error fetching surah details', error);
                setLoading(false);
            }
        };

        fetchSurah();
    }, [surahNumber]);

    useEffect(() => {
        // Scroll to initial ayah if provided
        if (!loading && initialAyah > 1 && ayahRefs.current[initialAyah]) {
            ayahRefs.current[initialAyah].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [loading, initialAyah]);

    const handlePlay = (index) => {
        setPlayingAyahIndex(index);
    };

    const handlePause = () => {
        setPlayingAyahIndex(null);
    };

    const handlePlayAll = () => {
        setPlayingAyahIndex(0);
    };

    // const [showCompletionModal, setShowCompletionModal] = useState(false); // Lifted to parent

    // ... (existing code)

    // Auto-play next ayah and track progress
    const handleAyahEnded = async (index) => {
        // Track progress for this single verse
        try {
            const isLastAyah = index === surahData.ayahs.length - 1;
            const ayahNumber = surahData.ayahs[index].numberInSurah;

            // 1. Record daily stats
            await apiClient.post('/quran/daily-reading', {
                surahsRead: isLastAyah ? [surahNumber] : [],
                ayahsCount: 1,
                timeSpent: 0,
            });

            // 2. Update Last Read Position
            await apiClient.put('/quran/progress', {
                lastReadSurah: surahNumber,
                lastReadAyah: ayahNumber,
            });

            if (isLastAyah) {
                setShowCompletionModal(true);
                setTimeout(() => setShowCompletionModal(false), 4000); // Auto hide after 4s
            }

            if (onProgressUpdate) onProgressUpdate(); // Refresh stats immediately
        } catch (error) {
            console.error('Error tracking verse progress', error);
        }

        if (index < surahData.ayahs.length - 1) {
            setPlayingAyahIndex(index + 1);
            // Optional: scroll to next ayah
            const nextAyahNum = surahData.ayahs[index + 1].numberInSurah;
            if (ayahRefs.current[nextAyahNum]) {
                ayahRefs.current[nextAyahNum].scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        } else {
            setPlayingAyahIndex(null); // Stop at the end
        }
    };

    const toggleBookmark = async (ayah) => {
        const isBookmarked = bookmarks.some(
            (b) => b.surahNumber === surahNumber && b.ayahNumber === ayah.numberInSurah
        );

        if (isBookmarked) {
            const bookmark = bookmarks.find(
                (b) => b.surahNumber === surahNumber && b.ayahNumber === ayah.numberInSurah
            );
            try {
                await apiClient.delete(`/quran/bookmarks/${bookmark._id}`);
                setBookmarks(bookmarks.filter((b) => b._id !== bookmark._id));
            } catch (error) {
                console.error('Error deleting bookmark', error);
            }
        } else {
            try {
                const res = await apiClient.post('/quran/bookmarks', {
                    surahNumber,
                    surahName: surahData.englishName,
                    ayahNumber: ayah.numberInSurah,
                });
                setBookmarks(res.data);
            } catch (error) {
                console.error('Error adding bookmark', error);
            }
        }
    };

    if (loading) return <div>Chargement de la sourate...</div>;

    return (
        <div className="quran-reader">
            {/* Completion Modal */}
            {showCompletionModal && (
                <div className="completion-modal-overlay">
                    <div className="completion-modal-content">
                        <FaTrophy className="completion-icon" />
                        <h2 className="completion-title">MashaAllah !</h2>
                        <p className="completion-subtitle">
                            Vous avez terminé la sourate <strong>{surahData.englishName}</strong> (- {surahData.name}).
                        </p>
                    </div>
                </div>
            )}

            <div className="surah-header">
                <h2>
                    {surahData.englishName} - {surahData.name}
                </h2>
                <p>
                    {surahData.englishNameTranslation} • {surahData.numberOfAyahs} Versets •{' '}
                    {surahData.revelationType === 'Meccan' ? 'Mecquoise' : 'Médinoise'}
                </p>
                <button className="btn" onClick={handlePlayAll}>
                    Tout écouter
                </button>

            </div>

            {surahNumber !== 1 && surahNumber !== 9 && (
                <div className="bismillah-header">
                    بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
                </div>
            )}

            <div className="ayahs-list">
                {surahData.ayahs.map((ayah, index) => (
                    <div
                        key={ayah.number}
                        ref={(el) => (ayahRefs.current[ayah.numberInSurah] = el)}
                    >
                        <AyahDisplay
                            ayah={ayah}
                            surah={surahData}
                            reciterId={reciterId}
                            isPlaying={playingAyahIndex === index}
                            onPlay={() => handlePlay(index)}
                            onPause={() => handlePause()}
                            onEnded={() => handleAyahEnded(index)}
                            isBookmarked={bookmarks.some(
                                (b) =>
                                    b.surahNumber === surahNumber &&
                                    b.ayahNumber === ayah.numberInSurah
                            )}
                            onToggleBookmark={() => toggleBookmark(ayah)}
                        />
                        {/* Hack to handle auto-play since AyahDisplay only exposes onPause */}
                        {playingAyahIndex === index && (
                            <div style={{ display: 'none' }}>
                                {/* Logic handled inside AyahDisplay via onEnded -> onPause. 
                     But we need to know if it ended naturally to play next.
                     Let's assume user manual pause stops chain.
                     We might need to refactor AyahDisplay to distinguish.
                     For now, let's leave auto-play as a future enhancement or simple implementation.
                 */}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default QuranReader;
